import { AfterViewInit, Component, computed, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';

import { InvestorService } from './investor.service';
import { CatalogLocationService } from '../shared/catalog-location.service';
import { CatalogCrudModalComponent } from '../shared/components/catalog-crud-modal';
import { CatalogDeleteConfirmModalComponent } from '../shared/components/catalog-delete-confirm-modal';
import { CatalogPageCardComponent } from '../shared/components/catalog-page-card';
import { CatalogTableToolbarComponent } from '../shared/components/catalog-table-toolbar';
import { CatalogDatatableController, DatatableViewStateSnapshot } from '../shared/catalog-datatable.controller';
import { CatalogModalController } from '../shared/catalog-modal.controller';
import {
  buildDocumentUploadToast,
  createDocumentUploadEntries,
  DocumentUploadEntry,
  summarizeDocumentUploads,
  updateDocumentUploadEntry,
  uploadDocumentsSequentially,
} from '../../shared/document-upload';
import { DocumentUploadList } from '../../shared/document-upload-list';
import { DocumentNameCell } from '../../shared/document-name-cell';
import { DocumentDescriptionCell } from '../../shared/document-description-cell';
import { resolveApiErrorMessage } from '../../shared/api-error';
import {
  CountryOption,
  CreateInvestorRequest,
  DocumentCreateRequest,
  DocumentUpdateRequest,
  DeleteBlocker,
  Investor as InvestorRow,
  InvestorDocumentItem,
  InvestorGroupOption,
  InvestorNoteItem,
  InvestorTypeOption,
  NoteUpsertRequest,
  RegionOption,
  UpdateInvestorRequest,
} from './investor.types';

type InvestorFormControlName = 'name' | 'investorTypeId' | 'countryId' | 'regionId' | 'investorGroupId';

interface InvestorFormValue {
  name: string;
  investorTypeId: string;
  countryId: string;
  regionId: string;
  investorGroupId: string;
}

interface InvestorDocumentFormValue {
  description: string;
  filename: string;
  files: File[];
}

interface InvestorNoteFormValue {
  note: string;
}

@Component({
  selector: 'app-investor',
  imports: [
    ReactiveFormsModule,
    CatalogPageCardComponent,
    CatalogTableToolbarComponent,
    CatalogCrudModalComponent,
    CatalogDeleteConfirmModalComponent,
    DocumentUploadList,
    DocumentNameCell,
    DocumentDescriptionCell,
  ],
  templateUrl: './investor.html',
  styleUrl: './investor.css',
})
export class Investor implements OnInit, AfterViewInit, OnDestroy {
  private readonly datatableId = 'kt_investor_datatable';
  private readonly destroyRef = inject(DestroyRef);
  private readonly createModalId = 'create_investor_modal';
  private readonly editModalId = 'edit_investor_modal';
  private readonly deleteConfirmModalId = 'delete_investor_confirm_modal';
  private readonly createSelectIds = [
    'create-investor-type',
    'create-investor-country',
    'create-investor-region',
    'create-investor-group',
  ];
  private readonly editSelectIds = [
    'edit-investor-type',
    'edit-investor-country',
    'edit-investor-region',
    'edit-investor-group',
  ];
  private readonly formBuilder = inject(FormBuilder);
  private readonly datatableController = new CatalogDatatableController(this.datatableId);
  private readonly modalController = new CatalogModalController();
  private readonly notesSource = 'Investor';
  private readonly documentSource = 'Investor';
  private readonly allowedDocumentTypes = ['.doc', '.msg', '.pdf', '.xls'];

  private createModalObserver?: MutationObserver;
  private editModalObserver?: MutationObserver;

  protected readonly createInvestorForm = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required]],
    investorTypeId: ['', [Validators.required]],
    countryId: ['', [Validators.required]],
    regionId: ['', [Validators.required]],
    investorGroupId: ['', [Validators.required]],
  });
  protected readonly editInvestorForm = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required]],
    investorTypeId: ['', [Validators.required]],
    countryId: ['', [Validators.required]],
    regionId: ['', [Validators.required]],
    investorGroupId: ['', [Validators.required]],
  });
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly investors = signal<InvestorRow[]>([]);
  protected readonly investorTypes = signal<InvestorTypeOption[]>([]);
  protected readonly countries = signal<CountryOption[]>([]);
  protected readonly regions = signal<RegionOption[]>([]);
  protected readonly investorGroups = signal<InvestorGroupOption[]>([]);
  protected readonly lookupError = signal('');
  protected readonly isCreating = signal(false);
  protected readonly createFormSubmitted = signal(false);
  protected readonly createFormError = signal('');
  protected readonly editingInvestorId = signal<number | null>(null);
  protected readonly isUpdating = signal(false);
  protected readonly editFormSubmitted = signal(false);
  protected readonly editFormError = signal('');
  protected readonly deletingInvestorId = signal<number | null>(null);
  protected readonly pendingDeleteInvestorId = signal<number | null>(null);
  protected readonly alertVariant = signal<'success' | 'destructive' | null>(null);
  protected readonly alertTitle = signal('');
  protected readonly alertMessage = signal('');
  protected readonly alertBlockers = signal<DeleteBlocker[]>([]);
  protected readonly documents = signal<InvestorDocumentItem[]>([]);
  protected readonly notes = signal<InvestorNoteItem[]>([]);
  protected readonly isMutatingDocument = signal(false);
  protected readonly renamingDocumentId = signal<number | null>(null);
  protected readonly describingDocumentId = signal<number | null>(null);
  protected readonly documentUploadEntries = signal<DocumentUploadEntry[]>([]);
  protected readonly isMutatingNote = signal(false);
  protected readonly documentFormError = signal('');
  protected readonly noteFormError = signal('');
  protected readonly editingDocumentId = signal<number | null>(null);
  protected readonly editingNoteId = signal<number | null>(null);
  protected readonly deletingDocumentId = signal<number | null>(null);
  protected readonly deletingNoteId = signal<number | null>(null);
  protected readonly isDocumentEditorVisible = signal(false);
  protected readonly isNoteEditorVisible = signal(false);
  protected readonly documentForm = signal<InvestorDocumentFormValue>({
    description: '',
    filename: '',
    files: [],
  });
  protected readonly noteForm = signal<InvestorNoteFormValue>({
    note: '',
  });
  protected readonly documentModalTitle = computed(() => this.editingDocumentId() === null ? 'Upload Documentation' : 'Edit Documentation');
  protected readonly documentModalSubmitLabel = computed(() => this.editingDocumentId() === null ? 'Upload Documents' : 'Save Changes');
  protected readonly noteModalTitle = computed(() => this.editingNoteId() === null ? 'Add Note' : 'Edit Note');
  protected readonly noteModalSubmitLabel = computed(() => this.editingNoteId() === null ? 'Add Note' : 'Save Changes');
  protected readonly hasAlert = computed(() => this.alertVariant() !== null);

  constructor(
    private readonly investorService: InvestorService,
    private readonly catalogLocationService: CatalogLocationService,
  ) {}

  ngOnInit(): void {
    this.loadInvestors();
    this.loadCreateModalLookups();
    this.bindCountryRegionAutoSelect();
  }

  ngAfterViewInit(): void {
    this.bindCreateModalCloseReset();
    this.bindEditModalCloseReset();
  }

  ngOnDestroy(): void {
    this.createModalObserver?.disconnect();
    this.editModalObserver?.disconnect();
  }

  protected shouldShowCreateFieldError(controlName: InvestorFormControlName): boolean {
    return this.createFormSubmitted() && this.createInvestorForm.controls[controlName].invalid;
  }

  protected shouldShowEditFieldError(controlName: InvestorFormControlName): boolean {
    return this.editFormSubmitted() && this.editInvestorForm.controls[controlName].invalid;
  }

  protected onCreateInvestor(): void {
    this.createFormSubmitted.set(true);
    this.createFormError.set('');

    if (this.createInvestorForm.invalid) {
      this.createInvestorForm.markAllAsTouched();
      return;
    }

    const payloadDraft = this.toInvestorPayloadDraft(this.createInvestorForm.getRawValue());
    if (!payloadDraft) {
      this.createFormError.set('Please select a valid value for all required dropdown fields.');
      return;
    }

    const payload: CreateInvestorRequest = {
      ...payloadDraft,
      createdBy: '',
    };

    this.isCreating.set(true);

    this.investorService.createInvestor(payload).subscribe({
      next: () => {
        this.isCreating.set(false);
        this.closeCreateInvestorModal();
        this.resetCreateInvestorForm();
        window.KTToast?.show({
          message: 'Investor created successfully.',
          variant: 'success',
        });
        this.loadInvestors();
      },
      error: () => {
        this.isCreating.set(false);
        this.createFormError.set('Unable to create investor at this time. Please try again.');
      },
    });
  }

  protected resetCreateInvestorForm(): void {
    this.createInvestorForm.reset(this.getEmptyInvestorFormValue());
    this.createFormSubmitted.set(false);
    this.createFormError.set('');
    this.documents.set([]);
    this.notes.set([]);
  }

  protected openEditInvestorModal(investor: InvestorRow): void {
    this.editingInvestorId.set(investor.id);
    this.editInvestorForm.reset({
      name: investor.name,
      investorTypeId: this.toSelectValue(investor.investorTypeId),
      countryId: this.toSelectValue(investor.countryId),
      regionId: this.toSelectValue(investor.regionId),
      investorGroupId: this.toSelectValue(investor.investorGroupId),
    });
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
    this.resetDocumentForm();
    this.resetNoteForm();
    this.isDocumentEditorVisible.set(false);
    this.isNoteEditorVisible.set(false);

    this.syncEditRegionWithCountry(this.editInvestorForm.controls.countryId.value);
    this.refreshSelectUiBatch(this.editSelectIds);
    this.initializeKtSelects();
    this.loadInvestorRelatedData(investor.id);
    this.modalController.open(this.editModalId);
  }

  protected onUpdateInvestor(): void {
    this.editFormSubmitted.set(true);
    this.editFormError.set('');

    if (this.editInvestorForm.invalid) {
      this.editInvestorForm.markAllAsTouched();
      return;
    }

    const investorId = this.editingInvestorId();
    if (investorId === null) {
      this.editFormError.set('Unable to identify the investor to update.');
      return;
    }

    const payload = this.toInvestorPayloadDraft(this.editInvestorForm.getRawValue());
    if (!payload) {
      this.editFormError.set('Please select a valid value for all required dropdown fields.');
      return;
    }

    const datatableSnapshot = this.safeCaptureDatatableViewState();

    this.isUpdating.set(true);

    this.investorService.updateInvestor(investorId, payload).subscribe({
      next: () => {
        this.isUpdating.set(false);
        this.closeEditInvestorModal();
        this.resetEditInvestorForm();
        window.KTToast?.show({
          message: 'Investor updated successfully.',
          variant: 'success',
        });
        this.loadInvestors(datatableSnapshot);
      },
      error: () => {
        this.isUpdating.set(false);
        this.editFormError.set('Unable to update investor at this time. Please try again.');
      },
    });
  }

  protected resetEditInvestorForm(): void {
    this.editInvestorForm.reset(this.getEmptyInvestorFormValue());
    this.editingInvestorId.set(null);
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
    this.documents.set([]);
    this.notes.set([]);
    this.isDocumentEditorVisible.set(false);
    this.isNoteEditorVisible.set(false);
  }

  protected trackDocument(_: number, item: InvestorDocumentItem): string {
    return `${item.id}-${item.filename}-${item.description}`;
  }

  protected trackNote(_: number, item: InvestorNoteItem): string {
    return `${item.id}-${item.dateModified}`;
  }

  protected resetDocumentForm(): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.documentFormError.set('');
    this.editingDocumentId.set(null);
    this.documentUploadEntries.set([]);
    this.documentForm.set({
      description: '',
      filename: '',
      files: [],
    });
  }

  protected documentFilenames(): string[] {
    return this.documents().map((document) => document.filename);
  }

  protected renameDocument(document: InvestorDocumentItem, filename: string): void {
    this.saveDocumentInline(document, 'filename', filename);
  }

  protected updateDocumentDescription(document: InvestorDocumentItem, description: string | null): void {
    this.saveDocumentInline(document, 'description', description);
  }

  private saveDocumentInline(document: InvestorDocumentItem, field: 'filename' | 'description', value: string | null): void {
    const parentId = this.currentInvestorParentId();
    if (!parentId || this.isMutatingDocument()) {
      return;
    }

    const payload: DocumentUpdateRequest = {
      parentElementId: parentId,
      securityId: null,
      description: field === 'description' ? value : document.description || null,
      type: document.type || null,
      filename: field === 'filename' ? (value ?? document.filename) : document.filename,
      ezShareId: document.ezShareId || null,
      ezShareLink: document.ezShareLink || null,
      source: this.documentSource,
    };

    this.isMutatingDocument.set(true);
    const savingDocumentId = field === 'filename' ? this.renamingDocumentId : this.describingDocumentId;
    savingDocumentId.set(document.id);
    this.investorService.updateDocument(document.id, payload).subscribe({
      next: () => {
        this.isMutatingDocument.set(false);
        savingDocumentId.set(null);
        this.loadInvestorDocuments(parentId);
        window.KTToast?.show({
          message: field === 'filename' ? `Document renamed to '${value}'.` : 'Document description updated.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingDocument.set(false);
        savingDocumentId.set(null);
        window.KTToast?.show({
          message: resolveApiErrorMessage(
            field === 'filename'
              ? 'Unable to rename document. Please try again.'
              : 'Unable to update document description. Please try again.',
            error,
          ),
          variant: 'destructive',
        });
      },
    });
  }

  protected startEditDocumentRow(row: InvestorDocumentItem): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.documentFormError.set('');
    this.editingDocumentId.set(row.id);
    this.documentForm.set({
      description: row.description,
      filename: row.filename,
      files: [],
    });
    this.isDocumentEditorVisible.set(true);
  }

  protected openCreateDocumentEditor(): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.resetDocumentForm();
    this.isDocumentEditorVisible.set(true);
  }

  protected closeDocumentEditor(): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.resetDocumentForm();
    this.isDocumentEditorVisible.set(false);
  }

  protected updateDocumentField(field: 'description' | 'filename', value: string): void {
    this.documentForm.update((current) => ({
      ...current,
      [field]: value,
    }));
    this.documentFormError.set('');
  }

  protected onDocumentFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];

    this.documentForm.update((current) => ({
      ...current,
      files,
    }));
    this.documentUploadEntries.set(createDocumentUploadEntries(files));
    this.documentFormError.set('');
  }

  protected addOrUpdateDocument(): void {
    const parentId = this.currentInvestorParentId();
    if (!parentId || this.isMutatingDocument()) {
      this.documentFormError.set('Save the investor first to manage documentation.');
      return;
    }

    const form = this.documentForm();
    const editingDocumentId = this.editingDocumentId();
    if (editingDocumentId !== null) {
      const filename = form.filename.trim();
      if (!filename) {
        this.documentFormError.set('Filename is required.');
        return;
      }

      const currentDocument = this.documents().find((document) => document.id === editingDocumentId);
      if (!currentDocument) {
        this.documentFormError.set('Unable to identify the document to update.');
        return;
      }

      const payload: DocumentUpdateRequest = {
        parentElementId: parentId,
        securityId: null,
        description: form.description.trim() || null,
        type: currentDocument.type || null,
        filename,
        ezShareId: currentDocument.ezShareId || null,
        ezShareLink: currentDocument.ezShareLink || null,
        source: this.documentSource,
      };

      this.isMutatingDocument.set(true);
      this.documentFormError.set('');
      this.investorService.updateDocument(editingDocumentId, payload).subscribe({
        next: () => {
          this.isMutatingDocument.set(false);
          this.resetDocumentForm();
          this.isDocumentEditorVisible.set(false);
          this.loadInvestorDocuments(parentId);
          window.KTToast?.show({ message: 'Document metadata updated successfully.', variant: 'success' });
        },
        error: () => {
          this.isMutatingDocument.set(false);
          this.documentFormError.set('Unable to update document. Please try again.');
        },
      });
      return;
    }

    if (form.files.length === 0) {
      this.documentFormError.set('Select at least one file to upload.');
      return;
    }

    const reservedFilenames = new Set(
      this.documents()
        .map((document) => document.filename.trim().toUpperCase())
        .filter((filename) => filename.length > 0),
    );
    const invalidFiles: string[] = [];
    const payloads: DocumentCreateRequest[] = [];

    for (const file of form.files) {
      const extension = this.extractFileExtension(file.name);
      if (!extension || !this.allowedDocumentTypes.includes(extension)) {
        invalidFiles.push(file.name || '(unknown file)');
        continue;
      }

      let generatedFilename = '';
      let attempts = 0;
      do {
        generatedFilename = `${this.generateDocumentCode()}.${extension.slice(1)}`.toUpperCase();
        attempts += 1;
      } while (reservedFilenames.has(generatedFilename) && attempts < 10);

      if (reservedFilenames.has(generatedFilename)) {
        this.documentFormError.set('Unable to generate a unique filename code. Please retry upload.');
        return;
      }
      reservedFilenames.add(generatedFilename);

      payloads.push({
        filename: generatedFilename,
        parentElementId: parentId,
        securityId: null,
        description: '',
        type: extension,
        source: this.documentSource,
        modifiedBy: this.resolveModifiedBy(),
        file,
      });
    }

    if (invalidFiles.length > 0) {
      this.documentFormError.set(
        `Unsupported file type: ${invalidFiles.join(', ')}. Allowed types: ${this.allowedDocumentTypes.join(', ')}.`,
      );
      return;
    }

    this.isMutatingDocument.set(true);
    this.documentFormError.set('');
    uploadDocumentsSequentially(
      payloads,
      (payload) => this.investorService.createDocument(payload),
      (file, status, message) =>
        this.documentUploadEntries.update((entries) => updateDocumentUploadEntry(entries, file, status, message)),
    ).subscribe((outcomes) => {
      const summary = summarizeDocumentUploads(outcomes);
      this.isMutatingDocument.set(false);
      this.loadInvestorDocuments(parentId);
      window.KTToast?.show(buildDocumentUploadToast(summary));

      if (summary.failed.length === 0) {
        this.resetDocumentForm();
        this.isDocumentEditorVisible.set(false);
        return;
      }

      this.documentForm.update((current) => ({ ...current, files: summary.retryableFiles }));
      if (summary.retryableFiles.length > 0) {
        this.documentFormError.set('Some files could not be uploaded. Upload again to retry only the failed files.');
      }
    });
  }

  protected requestDeleteDocumentRow(row: InvestorDocumentItem): void {
    const documentId = row.id;
    const parentId = this.currentInvestorParentId();
    if (documentId === null || !parentId || this.isMutatingDocument()) {
      return;
    }

    this.isMutatingDocument.set(true);
    this.deletingDocumentId.set(documentId);
    this.documentFormError.set('');
    this.investorService.deleteDocument(documentId).subscribe({
      next: () => {
        this.isMutatingDocument.set(false);
        this.deletingDocumentId.set(null);
        this.loadInvestorDocuments(parentId);
        window.KTToast?.show({ message: 'Document deleted successfully.', variant: 'success' });
      },
      error: () => {
        this.isMutatingDocument.set(false);
        this.deletingDocumentId.set(null);
        this.documentFormError.set('Unable to delete document. Please try again.');
      },
    });
  }

  protected resetNoteForm(): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.noteFormError.set('');
    this.editingNoteId.set(null);
    this.noteForm.set({ note: '' });
  }

  protected startEditNoteRow(row: InvestorNoteItem): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.noteFormError.set('');
    this.editingNoteId.set(row.id);
    this.noteForm.set({ note: row.description });
    this.isNoteEditorVisible.set(true);
  }

  protected openCreateNoteEditor(): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.resetNoteForm();
    this.isNoteEditorVisible.set(true);
  }

  protected closeNoteEditor(): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.resetNoteForm();
    this.isNoteEditorVisible.set(false);
  }

  protected updateNoteField(value: string): void {
    this.noteForm.set({ note: value });
    this.noteFormError.set('');
  }

  protected addOrUpdateNote(): void {
    const parentId = this.currentInvestorParentId();
    if (!parentId || this.isMutatingNote()) {
      this.noteFormError.set('Save the investor first to manage notes.');
      return;
    }

    const noteText = this.noteForm().note.trim();
    if (!noteText) {
      this.noteFormError.set('Note text is required.');
      return;
    }

    if (noteText.length > 1600) {
      this.noteFormError.set('Note text cannot exceed 1600 characters.');
      return;
    }

    const payload: NoteUpsertRequest = {
      parentId,
      source: this.notesSource,
      description: noteText,
      modifiedBy: this.resolveModifiedBy(),
    };

    const editingNoteId = this.editingNoteId();
    this.isMutatingNote.set(true);
    this.noteFormError.set('');
    if (editingNoteId === null) {
      this.investorService.createNote(payload).subscribe({
        next: () => {
          this.isMutatingNote.set(false);
          this.resetNoteForm();
          this.isNoteEditorVisible.set(false);
          this.loadInvestorNotes(parentId);
          window.KTToast?.show({ message: 'Note added successfully.', variant: 'success' });
        },
        error: () => {
          this.isMutatingNote.set(false);
          this.noteFormError.set('Unable to add note. Please try again.');
        },
      });
      return;
    }

    this.investorService.updateNote(editingNoteId, payload).subscribe({
      next: () => {
        this.isMutatingNote.set(false);
        this.resetNoteForm();
        this.isNoteEditorVisible.set(false);
        this.loadInvestorNotes(parentId);
        window.KTToast?.show({ message: 'Note updated successfully.', variant: 'success' });
      },
      error: () => {
        this.isMutatingNote.set(false);
        this.noteFormError.set('Unable to update note. Please try again.');
      },
    });
  }

  protected requestDeleteNoteRow(row: InvestorNoteItem): void {
    const noteId = row.id;
    const parentId = this.currentInvestorParentId();
    if (noteId === null || !parentId || this.isMutatingNote()) {
      return;
    }

    this.isMutatingNote.set(true);
    this.deletingNoteId.set(noteId);
    this.noteFormError.set('');
    this.investorService.deleteNote(noteId).subscribe({
      next: () => {
        this.isMutatingNote.set(false);
        this.deletingNoteId.set(null);
        this.loadInvestorNotes(parentId);
        window.KTToast?.show({ message: 'Note deleted successfully.', variant: 'success' });
      },
      error: () => {
        this.isMutatingNote.set(false);
        this.deletingNoteId.set(null);
        this.noteFormError.set('Unable to delete note. Please try again.');
      },
    });
  }

  protected loadInvestors(snapshot?: DatatableViewStateSnapshot): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.investorService.getInvestors().subscribe({
      next: (rows) => {
        this.investors.set(rows);
        this.isLoading.set(false);
        this.initializeKtDatatable(snapshot);
      },
      error: () => {
        this.errorMessage.set('Unable to load investor data from the API.');
        this.isLoading.set(false);
      },
    });
  }

  protected onTableActionClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const editButton = target?.closest('button[data-action="edit-investor"]') as HTMLButtonElement | null;
    const deleteButton = target?.closest('button[data-action="delete-investor"]') as HTMLButtonElement | null;

    if (editButton && !editButton.disabled) {
      const investorId = this.getInvestorIdFromButton(editButton);
      if (investorId === null) {
        return;
      }

      const investor = this.investors().find((item) => item.id === investorId);
      if (!investor) {
        return;
      }

      this.openEditInvestorModal(investor);
      return;
    }

    if (!deleteButton || deleteButton.disabled) {
      return;
    }

    const investorId = this.getInvestorIdFromButton(deleteButton);
    if (investorId === null) {
      return;
    }

    this.openDeleteConfirmationModal(investorId);
  }

  protected cancelDeleteInvestor(): void {
    this.pendingDeleteInvestorId.set(null);
    this.closeDeleteConfirmationModal();
  }

  protected confirmDeleteInvestor(): void {
    const investorId = this.pendingDeleteInvestorId();

    if (investorId === null || this.deletingInvestorId() !== null) {
      return;
    }

    this.closeDeleteConfirmationModal();
    this.pendingDeleteInvestorId.set(null);
    this.removeInvestor(investorId);
  }

  protected removeInvestor(investorId: number): void {
    if (this.deletingInvestorId() !== null) {
      return;
    }

    this.deletingInvestorId.set(investorId);

    this.investorService.deleteInvestor(investorId).subscribe({
      next: (result) => {
        this.deletingInvestorId.set(null);

        if (result.kind === 'success') {
          const snapshot = this.safeCaptureDatatableViewState();
          this.investors.update((rows) => rows.filter((item) => item.id !== investorId));
          this.showSuccessAlert('Investor deleted successfully.');
          this.initializeKtDatatable(snapshot);
          return;
        }

        if (result.kind === 'conflict') {
          this.showDestructiveAlert(
            result.payload.title || 'Investor cannot be deleted.',
            result.payload.detail || 'Investor is linked to bond investor records. Resolve linked TradeId/DealId pairs before deleting.',
            result.payload.blockers,
          );
          return;
        }

        this.showDestructiveAlert(
          'Unable to delete investor.',
          result.message,
          [],
        );
      },
      error: () => {
        this.deletingInvestorId.set(null);
        this.showDestructiveAlert(
          'Unable to delete investor.',
          'Unable to delete investor at this time. Please try again.',
          [],
        );
      },
    });
  }

  protected dismissAlert(): void {
    this.alertVariant.set(null);
    this.alertTitle.set('');
    this.alertMessage.set('');
    this.alertBlockers.set([]);
  }

  protected formatDateTime(value: string): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  }

  private loadInvestorRelatedData(investorId: number): void {
    const parentId = String(investorId);
    this.loadInvestorDocuments(parentId);
    this.loadInvestorNotes(parentId);
  }

  private loadInvestorDocuments(parentId: string): void {
    this.investorService.getDocumentsByParent(parentId).subscribe({
      next: (documents) => {
        const source = this.documentSource.toUpperCase();
        const filtered = documents.filter((item) => {
          return item.parentElementId.trim() === parentId
            && (!item.source.trim() || item.source.trim().toUpperCase() === source);
        });
        this.documents.set(filtered);
      },
      error: () => {
        this.documents.set([]);
      },
    });
  }

  private loadInvestorNotes(parentId: string): void {
    this.investorService.getNotes().subscribe({
      next: (notes) => {
        const source = this.notesSource.toUpperCase();
        const filtered = notes.filter((item) => {
          return item.parentId.trim() === parentId
            && item.source.trim().toUpperCase() === source;
        });
        this.notes.set(filtered);
      },
      error: () => {
        this.notes.set([]);
      },
    });
  }

  private loadCreateModalLookups(): void {
    this.lookupError.set('');

    forkJoin({
      investorTypes: this.investorService.getInvestorTypes(),
      countries: this.catalogLocationService.getCountries(),
      regions: this.catalogLocationService.getRegions(),
      investorGroups: this.investorService.getInvestorGroups(),
    }).subscribe({
      next: ({ investorTypes, countries, regions, investorGroups }) => {
        this.investorTypes.set(investorTypes);
        this.countries.set(countries);
        this.regions.set(regions);
        this.investorGroups.set(investorGroups);
        this.syncRegionWithCountry(this.createInvestorForm.controls.countryId.value);
        this.initializeKtSelects();
      },
      error: () => {
        this.lookupError.set('Unable to load lookup data for investor creation.');
      },
    });
  }

  private initializeKtSelects(): void {
    requestAnimationFrame(() => {
      window.KTSelect?.createInstances();
    });
  }

  private bindCountryRegionAutoSelect(): void {
    // Region is derived from country for this screen, so keep them synchronized on country changes.
    this.createInvestorForm.controls.countryId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((countryIdValue) => {
        this.syncRegionWithCountry(countryIdValue);
      });

    this.editInvestorForm.controls.countryId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((countryIdValue) => {
        this.syncEditRegionWithCountry(countryIdValue);
      });
  }

  private syncRegionWithCountry(countryIdValue: string): void {
    this.syncRegionControlWithCountry(countryIdValue, this.createInvestorForm.controls.regionId, 'create-investor-region');
  }

  private syncEditRegionWithCountry(countryIdValue: string): void {
    this.syncRegionControlWithCountry(countryIdValue, this.editInvestorForm.controls.regionId, 'edit-investor-region');
  }

  private syncRegionControlWithCountry(
    countryIdValue: string,
    regionControl: FormControl<string>,
    regionSelectId: string,
  ): void {
    const countryId = Number(countryIdValue);
    const selectedCountry = Number.isFinite(countryId)
      ? this.countries().find((item) => item.id === countryId)
      : undefined;

    regionControl.setValue(
      selectedCountry && selectedCountry.regionId !== null
        ? String(selectedCountry.regionId)
        : '',
    );

    this.refreshSelectUi(regionSelectId);
  }

  private bindCreateModalCloseReset(): void {
    this.createModalObserver = this.modalController.observeClose(this.createModalId, () => {
      this.handleCreateModalClosed();
    });
  }

  private bindEditModalCloseReset(): void {
    this.editModalObserver = this.modalController.observeClose(this.editModalId, () => {
      this.handleEditModalClosed();
    });
  }

  private handleCreateModalClosed(): void {
    this.resetCreateInvestorForm();
    this.clearSelectUiState(this.createSelectIds);
  }

  private handleEditModalClosed(): void {
    this.resetEditInvestorForm();
    this.clearSelectUiState(this.editSelectIds);
  }

  private clearSelectUiState(selectIds: readonly string[]): void {
    selectIds.forEach((selectId) => {
      const selectElement = document.getElementById(selectId) as HTMLSelectElement | null;
      if (!selectElement) {
        return;
      }

      selectElement.value = '';
      selectElement.dispatchEvent(new Event('change', { bubbles: true }));

      const instance = window.KTSelect?.getInstance?.(selectElement) as KTSelectInstance | undefined;
      instance?.showAllOptions?.();
      instance?.closeDropdown?.();
      instance?.refresh?.();
    });

    this.initializeKtSelects();
  }

  private refreshSelectUiBatch(selectIds: readonly string[]): void {
    selectIds.forEach((selectId) => {
      this.refreshSelectUi(selectId);
    });
  }

  private refreshSelectUi(selectId: string): void {
    const selectElement = document.getElementById(selectId) as HTMLSelectElement | null;
    if (!selectElement) {
      return;
    }

    selectElement.dispatchEvent(new Event('change', { bubbles: true }));
    const instance = window.KTSelect?.getInstance?.(selectElement) as KTSelectInstance | undefined;
    instance?.refresh?.();
  }

  private closeCreateInvestorModal(): void {
    this.modalController.close(this.createModalId);
  }

  private closeEditInvestorModal(): void {
    this.modalController.close(this.editModalId);
  }

  private openDeleteConfirmationModal(investorId: number): void {
    this.pendingDeleteInvestorId.set(investorId);
    this.modalController.open(this.deleteConfirmModalId);
  }

  private closeDeleteConfirmationModal(): void {
    this.modalController.close(this.deleteConfirmModalId);
  }

  private showSuccessAlert(message: string): void {
    this.alertVariant.set('success');
    this.alertTitle.set(message);
    this.alertMessage.set('');
    this.alertBlockers.set([]);
  }

  private showDestructiveAlert(title: string, message: string, blockers: DeleteBlocker[]): void {
    this.alertVariant.set('destructive');
    this.alertTitle.set(title);
    this.alertMessage.set(message);
    this.alertBlockers.set(blockers);
  }

  private initializeKtDatatable(snapshot?: DatatableViewStateSnapshot): void {
    this.datatableController.initialize(snapshot);
  }

  private safeCaptureDatatableViewState(): DatatableViewStateSnapshot {
    return this.datatableController.captureViewState();
  }

  private getInvestorIdFromButton(button: HTMLButtonElement): number | null {
    const investorIdRaw = button.getAttribute('data-investor-id');
    const investorId = investorIdRaw ? Number(investorIdRaw) : Number.NaN;

    return Number.isFinite(investorId) ? investorId : null;
  }

  private getEmptyInvestorFormValue(): InvestorFormValue {
    return {
      name: '',
      investorTypeId: '',
      countryId: '',
      regionId: '',
      investorGroupId: '',
    };
  }

  private toInvestorPayloadDraft(rawValue: InvestorFormValue): UpdateInvestorRequest | null {
    const investorTypeId = this.parsePositiveId(rawValue.investorTypeId);
    const countryId = this.parsePositiveId(rawValue.countryId);
    const regionId = this.parsePositiveId(rawValue.regionId);
    const investorGroupId = this.parsePositiveId(rawValue.investorGroupId);

    if (
      investorTypeId === null
      || countryId === null
      || regionId === null
      || investorGroupId === null
    ) {
      return null;
    }

    const payload: UpdateInvestorRequest = {
      name: rawValue.name.trim(),
      investorTypeId,
      countryId,
      regionId,
      investorGroupId,
    };

    return payload;
  }

  private parsePositiveId(rawId: string): number | null {
    const parsedId = Number(rawId);

    return Number.isFinite(parsedId) && parsedId > 0
      ? parsedId
      : null;
  }

  private currentInvestorParentId(): string | null {
    const investorId = this.editingInvestorId();
    if (investorId === null) {
      return null;
    }

    return String(investorId);
  }

  private resolveModifiedBy(): string {
    return 'FRIDA UI';
  }

  private extractFileExtension(filename: string): string | null {
    const trimmed = filename.trim();
    const lastDot = trimmed.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === trimmed.length - 1) {
      return null;
    }

    return trimmed.slice(lastDot).toLowerCase();
  }

  private generateDocumentCode(length = 12): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let index = 0; index < length; index += 1) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      result += charset[randomIndex];
    }

    return result;
  }

  private toSelectValue(value: number): string {
    return Number.isFinite(value) && value > 0 ? String(value) : '';
  }
}
